import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { paymentService, SubscriptionStatus, UploadLimits } from '../services/paymentService';
import { useAuth } from '../components/auth/AuthContext';
import { getUser } from '../utils/localStorageManager';
import { SubscriptionLimit } from '../components/Subscriptions/types';
import { familyPlanService, FamilyPlanStatus } from '../services/familyPlanService';

interface UserSubscriptionContextValue {
  subscriptionStatus: SubscriptionStatus | null;
  uploadLimits: UploadLimits | null;
  tierLimits: Map<string, SubscriptionLimit> | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isFamilyOwner: boolean;
  isFamilyMember: boolean;
  familyPlanStatus: FamilyPlanStatus | null;
}

const UserSubscriptionContext = createContext<UserSubscriptionContextValue | undefined>(undefined);

interface UserSubscriptionProviderProps {
  children: ReactNode;
}

export function UserSubscriptionProvider({ children }: UserSubscriptionProviderProps) {
  const { isAuthenticated } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [uploadLimits, setUploadLimits] = useState<UploadLimits | null>(null);
  const [tierLimits, setTierLimits] = useState<Map<string, SubscriptionLimit> | null>(null);
  const [familyPlanStatus, setFamilyPlanStatus] = useState<FamilyPlanStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Standard tiers to fetch limits for (static configuration)
      // Includes early_supporter for lifetime access features and family for family plans
      const standardTiers = ['free', 'starter', 'pro', 'family', 'early_supporter'];
      
      // Fetch subscription status, upload limits, tier limits, and family plan status in parallel
      const [statusResult, limitsResult, tierLimitsResults, familyPlanResult] = await Promise.allSettled([
        paymentService.getSubscriptionStatus(),
        paymentService.getUploadLimits(),
        // Fetch tier limits for all standard tiers
        Promise.allSettled(
          standardTiers.map(tier =>
            paymentService.getSubscriptionLimits(tier)
              .then(limits => ({ tier, limits }))
              .catch(err => {
                console.debug(`[Subscription] Failed to fetch limits for ${tier}:`, err?.message || err);
                return { tier, limits: null };
              })
          )
        ),
        // Fetch family plan status
        familyPlanService.getStatus(),
      ]);

      // Handle subscription status
      if (statusResult.status === 'fulfilled') {
        setSubscriptionStatus(statusResult.value);
      } else {
        console.error('Error fetching subscription status:', statusResult.reason);
        // Set default on error - features are now dynamic, use empty object
        setSubscriptionStatus({
          subscription_tier: 'free',
          subscription_status: 'inactive',
          is_complimentary: false,
          features: {}, // Empty - features come from API dynamically
        });
      }

      // Handle upload limits
      if (limitsResult.status === 'fulfilled') {
        setUploadLimits(limitsResult.value);
      } else {
        console.error('Error fetching upload limits:', limitsResult.reason);
      }

      // Handle tier limits
      if (tierLimitsResults.status === 'fulfilled') {
        const limitsMap = new Map<string, SubscriptionLimit>();
        tierLimitsResults.value.forEach(result => {
          if (result.status === 'fulfilled') {
            // result.value is { tier, limits } where limits is the API response
            const limits = result.value.limits;
            // Skip tiers where limits fetch failed (limits is null from catch handler)
            if (!limits) return;
            // Features are now dynamic from database - use as-is from API
            const normalized: SubscriptionLimit = {
              tier: result.value.tier,
              daily_instant_upload_limit: limits?.daily_instant_upload_limit ?? null,
              daily_scheduled_upload_limit: limits?.daily_scheduled_upload_limit ?? null,
              allow_scheduled_uploads: limits?.allow_scheduled_uploads ?? false,
              allow_upload_defaults: limits?.allow_upload_defaults ?? false,
              features: limits?.features ?? {}, // Pass through API features directly
            };
            limitsMap.set(result.value.tier, normalized);
          } else {
            console.error(`Error fetching tier limits for tier:`, result.reason);
          }
        });
        setTierLimits(limitsMap);
      } else {
        console.error('Error fetching tier limits:', tierLimitsResults.reason);
        setTierLimits(null);
      }

      // Handle family plan status
      if (familyPlanResult.status === 'fulfilled') {
        setFamilyPlanStatus(familyPlanResult.value);
      } else {
        // Not an error - user might not have a family plan
        // Only log if it's not a 404 or 403 (expected for non-family users)
        const status = (familyPlanResult.reason as any)?.response?.status;
        if (status && status !== 404 && status !== 403) {
          console.error('Error fetching family plan status:', familyPlanResult.reason);
        }
        setFamilyPlanStatus(null);
      }

      // Set error if all critical requests failed
      if (statusResult.status === 'rejected' && limitsResult.status === 'rejected' && tierLimitsResults.status === 'rejected') {
        setError('Failed to load subscription data');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load subscription data';
      setError(errorMessage);
      console.error('Error fetching subscription data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle authentication state changes and user ID tracking
  useEffect(() => {
    if (!isAuthenticated) {
      // User logged out - clear all data
      setSubscriptionStatus(null);
      setUploadLimits(null);
      setTierLimits(null);
      setFamilyPlanStatus(null);
      setIsLoading(false);
      setError(null);
      setCurrentUserId(null);
      return;
    }

    // User is authenticated - check if user ID changed
    const user = getUser();
    const userId = user?.id || null;
    
    if (userId && userId !== currentUserId) {
      // User ID changed - update and fetch new data
      setCurrentUserId(userId);
      fetchData();
    } else if (!userId && currentUserId) {
      // User ID cleared but we were authenticated - clear state
      setCurrentUserId(null);
      setSubscriptionStatus(null);
      setUploadLimits(null);
      setTierLimits(null);
      setFamilyPlanStatus(null);
      setIsLoading(false);
    } else if (userId && !currentUserId) {
      // First time loading for this user
      setCurrentUserId(userId);
      fetchData();
    }
  }, [isAuthenticated, currentUserId, fetchData]);

  // Compute family plan status from family plan API response
  const isFamilyOwner = familyPlanStatus?.is_owner ?? false;
  const isFamilyMember = (familyPlanStatus?.is_member && !familyPlanStatus?.is_owner) ?? false;

  const value: UserSubscriptionContextValue = {
    subscriptionStatus,
    uploadLimits,
    tierLimits,
    isLoading,
    error,
    refresh: fetchData,
    isFamilyOwner,
    isFamilyMember,
    familyPlanStatus,
  };

  return (
    <UserSubscriptionContext.Provider value={value}>
      {children}
    </UserSubscriptionContext.Provider>
  );
}

export function useUserSubscription(): UserSubscriptionContextValue {
  const context = useContext(UserSubscriptionContext);
  if (context === undefined) {
    throw new Error('useUserSubscription must be used within a UserSubscriptionProvider');
  }
  return context;
}

